/**
 * Workspace Service
 * 
 * Handles all workspace-related API calls.
 * Workspaces allow users to organize their financial data into separate contexts
 * (personal, business, association).
 */

import api from './api';
import {
  ApiResponse,
  Workspace,
  WorkspaceWithRole,
  WorkspaceMember,
  WorkspaceInvitation,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  InviteToWorkspaceRequest,
  UpdateWorkspaceMemberRequest,
  AcceptWorkspaceInvitationResponse,
} from '@budgira/shared';

/**
 * Response from switching workspace - includes workspace info and updated sharedAccess
 */
export interface SwitchWorkspaceResponse {
  workspace: WorkspaceWithRole;
  sharedAccess: {
    canManageCategories: boolean;
    canManagePaymentMethods: boolean;
    accountPermissions: Array<{ accountId: string; permission: 'none' | 'read' | 'write' }>;
    ownerEmail?: string;
    effectiveRole?: 'free' | 'premium' | 'admin';
  } | null;
}

export const workspaceService = {
  /**
   * Get all workspaces the current user has access to
   */
  async list(): Promise<ApiResponse<WorkspaceWithRole[]>> {
    const response = await api.get<ApiResponse<WorkspaceWithRole[]>>('/workspaces');
    return response.data;
  },

  /**
   * Get the current active workspace
   */
  async getCurrent(): Promise<ApiResponse<WorkspaceWithRole>> {
    const response = await api.get<ApiResponse<WorkspaceWithRole>>('/workspaces/current');
    return response.data;
  },

  /**
   * Get a specific workspace by ID
   */
  async getById(id: string): Promise<ApiResponse<WorkspaceWithRole>> {
    const response = await api.get<ApiResponse<WorkspaceWithRole>>(`/workspaces/${id}`);
    return response.data;
  },

  /**
   * Create a new workspace
   */
  async create(data: CreateWorkspaceRequest): Promise<ApiResponse<Workspace>> {
    const response = await api.post<ApiResponse<Workspace>>('/workspaces', data);
    return response.data;
  },

  /**
   * Update a workspace
   */
  async update(id: string, data: UpdateWorkspaceRequest): Promise<ApiResponse<Workspace>> {
    const response = await api.put<ApiResponse<Workspace>>(`/workspaces/${id}`, data);
    return response.data;
  },

  /**
   * Delete a workspace
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/workspaces/${id}`);
    return response.data;
  },

  /**
   * Switch to a different workspace
   * Returns both workspace info and updated sharedAccess for the user
   */
  async switchWorkspace(workspaceId: string): Promise<ApiResponse<SwitchWorkspaceResponse>> {
    const response = await api.post<ApiResponse<SwitchWorkspaceResponse>>(
      `/workspaces/switch/${workspaceId}`
    );
    return response.data;
  },

  // ===== Members =====

  /**
   * Get members of a workspace
   */
  async getMembers(workspaceId: string): Promise<ApiResponse<WorkspaceMember[]>> {
    const response = await api.get<ApiResponse<WorkspaceMember[]>>(
      `/workspaces/${workspaceId}/members`
    );
    return response.data;
  },

  /**
   * Update a member's role
   */
  async updateMember(
    workspaceId: string,
    memberId: string,
    data: UpdateWorkspaceMemberRequest
  ): Promise<ApiResponse<WorkspaceMember>> {
    const response = await api.put<ApiResponse<WorkspaceMember>>(
      `/workspaces/${workspaceId}/members/${memberId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove a member from workspace
   */
  async removeMember(workspaceId: string, memberId: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(
      `/workspaces/${workspaceId}/members/${memberId}`
    );
    return response.data;
  },

  // ===== Invitations =====

  /**
   * Get invitations for a workspace
   */
  async getInvitations(workspaceId: string): Promise<ApiResponse<WorkspaceInvitation[]>> {
    const response = await api.get<ApiResponse<WorkspaceInvitation[]>>(
      `/workspaces/${workspaceId}/invitations`
    );
    return response.data;
  },

  /**
   * Send an invitation to join a workspace
   */
  async sendInvitation(
    workspaceId: string,
    data: InviteToWorkspaceRequest
  ): Promise<ApiResponse<WorkspaceInvitation>> {
    const response = await api.post<ApiResponse<WorkspaceInvitation>>(
      `/workspaces/${workspaceId}/invitations`,
      data
    );
    return response.data;
  },

  /**
   * Cancel/revoke an invitation
   */
  async cancelInvitation(workspaceId: string, invitationId: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(
      `/workspaces/${workspaceId}/invitations/${invitationId}`
    );
    return response.data;
  },

  /**
   * Get pending invitations for the current user
   */
  async getPendingInvitations(): Promise<ApiResponse<(WorkspaceInvitation & { workspaceName: string })[]>> {
    const response = await api.get<ApiResponse<(WorkspaceInvitation & { workspaceName: string })[]>>(
      '/workspaces/invitations/pending'
    );
    return response.data;
  },

  /**
   * Accept a workspace invitation
   */
  async acceptInvitation(token: string): Promise<ApiResponse<AcceptWorkspaceInvitationResponse>> {
    const response = await api.post<ApiResponse<AcceptWorkspaceInvitationResponse>>(
      `/workspaces/invitations/${token}/accept`
    );
    return response.data;
  },

  /**
   * Update the current user's display name for a workspace.
   * This allows personalizing how the workspace appears in the list.
   * @param workspaceId - The workspace to rename (for this user only)
   * @param displayName - The custom name, or null to reset to the original name
   */
  async updateDisplayName(workspaceId: string, displayName: string | null): Promise<ApiResponse<null>> {
    const response = await api.put<ApiResponse<null>>(
      `/workspaces/${workspaceId}/display-name`,
      { displayName }
    );
    return response.data;
  },
};

export default workspaceService;
